import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import trans, { formatSize } from "../../../utils";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import Button from "../Button/Button";
import { toast } from "react-toastify";

const FileUploader = ({
	onFileSelect,
	file,
	multiple = false,
	acceptType,
	sizeMB = 5,
	label,
}) => {
	const maxFileSize = sizeMB * 1024 * 1024;

	const onDrop = useCallback(
		(acceptedFiles) => {
			if (!acceptedFiles || acceptedFiles.length === 0) return;

			const existingFiles = multiple ? file || [] : file ? [file] : [];

			const newValidFiles = [];
			acceptedFiles.forEach((newFile) => {
				const isDuplicate = existingFiles.some(
					(existingFile) =>
						existingFile.name === newFile.name &&
						existingFile.size === newFile.size
				);

				const isTooLarge = newFile.size > 5 * 1024 * 1024;

				if (isDuplicate) {
					toast.error(`${newFile.name} ${trans("is already uploaded")}.`, {
						autoClose: 800,
						rtl: localStorage.getItem("lang") === "ar" ? true : false,
					});
				} else if (isTooLarge) {
					toast.error(
						`${newFile.name} ${trans("is larger than")} ${sizeMB} MB.`,
						{
							autoClose: 800,
							rtl: localStorage.getItem("lang") === "ar" ? true : false,
						}
					);
				} else {
					newValidFiles.push(newFile);
				}
			});

			if (newValidFiles.length === 0) return;

			if (multiple) {
				onFileSelect?.([...existingFiles, ...newValidFiles]);
			} else {
				onFileSelect?.(newValidFiles[0]);
			}
		},
		[file, onFileSelect, multiple, maxFileSize, sizeMB]
	);

	const { getRootProps, getInputProps } = useDropzone({
		onDrop,
		multiple,
		accept: acceptType ? { [acceptType]: [] } : undefined,
		maxSize: maxFileSize,
	});

	const fileList = multiple ? file || [] : file ? [file] : [];

	const getFileIconUrl = (type = "") => {
		const lowerType = type.toLowerCase();
		if (lowerType === "image/png") {
			return `${process.env.PUBLIC_URL}/assets/PNG.svg`;
		} else if (lowerType === "image/jpg" || lowerType === "image/jpeg") {
			return `${process.env.PUBLIC_URL}/assets/JPG.svg`;
		} else if (lowerType === "application/pdf") {
			return `${process.env.PUBLIC_URL}/assets/PDF.svg`;
		} else {
			return `${process.env.PUBLIC_URL}/assets/Excel.svg`;
		}
	};

	return (
		<>
			{label ? (
				<label>{label}</label>
			) : (
				<p className="fs-16 fw-700 brand-700 text-uppercase">
					{trans("Attachments")}
				</p>
			)}
			<div className="file-uploader border rounded p-3 bg-light">
				<div {...getRootProps()} className="cursor-pointer">
					<input {...getInputProps()} />
					<div className="text-center">
						{fileList.length > 0 ? (
							fileList.map((f, index) => (
								<div
									key={index}
									className="d-flex align-items-center justify-content-between border p-2 mb-2 bg-white"
								>
									<div className="d-flex align-items-center">
										<img
											src={getFileIconUrl(f.type)}
											alt={f.type}
											style={{ width: 40 }}
											className="me-3"
										/>
										<div>
											<div
												className="fw-medium text-truncate"
												style={{ maxWidth: "200px" }}
											>
												{f.name}
											</div>
											<div className="text-muted small">
												{formatSize(f.size)}
											</div>
										</div>
									</div>
									<Button
										htmlType="button"
										type="text"
										icon={<DeleteOutlined />}
										className={"remove-skill"}
										danger
										onClick={(e) => {
											e.stopPropagation();
											if (onFileSelect) {
												if (multiple) {
													const updated = fileList.filter(
														(_, i) => i !== index
													);
													onFileSelect(updated.length > 0 ? updated : null);
												} else {
													onFileSelect(null);
												}
											}
										}}
									/>
								</div>
							))
						) : (
							<div className="p-3 border bg-white">
								<div className="mx-auto mb-2">
									<UploadOutlined width={64} />
								</div>
								<p>
									{trans("Drag files here or")}
									<span style={{ color: "#2E9245", fontWeight: 500 }}>
										{trans("Upload")}
									</span>
								</p>
								<p className="text-muted small mb-0">
									{acceptType || "Any file type"} (max {formatSize(maxFileSize)}
									)
								</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
};

export default FileUploader;
